import { AodRule } from '@app/modules/audit/modules/aod/aod.types';
import { Box, Chip, Grid } from '@mui/material';
import { useIntl } from 'react-intl';
import { LDateTime } from '../../../../../../../components/date/LDateTime';
import { ReportTypeChip } from '../../../../../../../components/ReportTypeChip';
import { UserDisplay, UsersChipList } from '../../../../../../../components/users';
import { notificationPolicyMessages, triggerMessages } from '../../../messages';
import { InfoM } from './InfoM';

interface AodRuleInfoProps {
  rule: AodRule;
}

export function AodRuleInfo({ rule }: AodRuleInfoProps) {
  const { formatMessage } = useIntl();

  const getTriggerLabel = (trigger: string) => {
    const message = triggerMessages[trigger];
    return message ? formatMessage(message) : trigger;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <InfoM
            label={formatMessage({
              id: 'audit.aod.rules.field.name',
              defaultMessage: 'Nombre',
            })}
          >
            {rule.name}
          </InfoM>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoM
            label={formatMessage({
              id: 'audit.aod.rules.field.reportType',
              defaultMessage: 'Tipo de reporte',
            })}
          >
            <ReportTypeChip reportType={rule.reportType} />
          </InfoM>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoM
            label={formatMessage({
              id: 'audit.aod.rules.field.trigger',
              defaultMessage: 'Trigger',
            })}
          >
            {getTriggerLabel(rule.trigger)}
          </InfoM>
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoM
            label={formatMessage({
              id: 'audit.aod.rules.field.active',
              defaultMessage: 'Estado',
            })}
          >
            <Chip
              label={
                rule.active
                  ? formatMessage({
                      id: 'common.status.active',
                      defaultMessage: 'Activa',
                    })
                  : formatMessage({
                      id: 'common.status.inactive',
                      defaultMessage: 'Inactiva',
                    })
              }
              color={rule.active ? 'success' : 'default'}
              size="small"
            />
          </InfoM>
        </Grid>

        {!!rule.managers?.length && (
          <Grid item xs={12} md={12}>
            <InfoM
              label={formatMessage({
                id: 'audit.aod.rules.managers',
                defaultMessage: 'Responsables',
              })}
            >
              <UsersChipList
                userIds={rule.managers
                  .map((manager) => manager.userId)
                  .filter((id): id is string => !!id)}
              />
            </InfoM>
          </Grid>
        )}

        <Grid item xs={12} md={12}>
          <InfoM label={formatMessage(notificationPolicyMessages.label)}>
            {formatMessage(
              notificationPolicyMessages[
                rule.notificationSettings?.policy as keyof typeof notificationPolicyMessages
              ] ?? {
                id: `audit.aod.rules.notificationPolicy.${rule.notificationSettings?.policy}`,
                defaultMessage: rule.notificationSettings?.policy ?? '—',
              },
            )}
          </InfoM>
        </Grid>

        <Grid item xs={12} md={12}>
          <InfoM
            label={formatMessage({
              id: 'audit.aod.rules.field.createdAt',
              defaultMessage: 'Creada',
            })}
          >
            <Box display="flex" gap={0.5}>
              <LDateTime value={rule.createdAt} />
              {rule.createdById && (
                <Box display="flex" gap={1}>
                  {formatMessage({
                    id: 'audit.aod.rules.createdBy',
                    defaultMessage: 'por',
                  })}{' '}
                  <strong>
                    <UserDisplay id={rule.createdById} />
                  </strong>
                </Box>
              )}
            </Box>
          </InfoM>
        </Grid>
      </Grid>
    </Box>
  );
}
